import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Upload, BookOpen, Plus, Eye, CheckCircle, XCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const ContentAdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('markers');
  const [markers, setMarkers] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [textbooks, setTextbooks] = useState([]);
  const [showMarkerDialog, setShowMarkerDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markerPreview, setMarkerPreview] = useState(null);
  const [modelPreview, setModelPreview] = useState(null);
  const [boards, setBoards] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  // Cascading filter state
  const [filterBoard, setFilterBoard] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTextbook, setFilterTextbook] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      // Load all data filtered by tenant
      const [markersRes, chaptersRes, quizzesRes, textbooksRes, boardsRes, classesRes, subjectsRes] = await Promise.all([
        api.get('/markers'),
        api.get('/chapters'),
        api.get('/quizzes'),
        api.get('/textbooks'),
        api.get('/boards'),
        api.get('/classes'),
        api.get('/subjects'),
      ]);
      setMarkers(markersRes.data.map(m => ({
        ...m,
        image_url: m.image_url || `https://placehold.co/400x300/4F46E5/white?text=${encodeURIComponent(m.concept_title || 'AR Marker')}`
      })));
      setChapters(chaptersRes.data);
      setQuizzes(quizzesRes.data);
      setTextbooks(textbooksRes.data);
      setBoards(boardsRes.data);
      setClasses(classesRes.data);
      setSubjects(subjectsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    }
  };

  const handleUploadMarker = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.post('/markers/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Marker and 3D model uploaded successfully!');
      setShowMarkerDialog(false);
      setMarkerPreview(null);
      setModelPreview(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    }
  };

  const handlePublishMarker = async (markerId, isPublished) => {
    try {
      const formData = new FormData();
      formData.append('is_published', !isPublished);
      await api.put(`/markers/${markerId}/publish`, formData);
      toast.success(isPublished ? 'Marker unpublished' : 'Marker published');
      loadData();
    } catch (error) {
      toast.error('Failed to update marker status');
    }
  };

  const handleMarkerFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setMarkerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleModelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setModelPreview(file.name);
    }
  };

  const handleCreateQuiz = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const questions = [];
    for (let i = 0; i < 3; i++) {
      const questionText = formData.get(`question_${i}`);
      if (questionText) {
        questions.push({
          id: `q_${Date.now()}_${i}`,
          text: questionText,
          options: [
            formData.get(`q${i}_option_0`),
            formData.get(`q${i}_option_1`),
            formData.get(`q${i}_option_2`),
            formData.get(`q${i}_option_3`),
          ],
          correct_option: parseInt(formData.get(`q${i}_correct`)),
        });
      }
    }

    try {
      await api.post('/quizzes', {
        marker_id: formData.get('marker_id'),
        questions,
      });
      toast.success('Quiz created successfully!');
      setShowQuizDialog(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create quiz');
    }
  };

  return (
    <Layout title="Content Admin Dashboard">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-indigo-900">Content Management</h1>
            <p className="text-slate-600 text-sm sm:text-base">Manage AR learning content for your tenant</p>
          </div>
        </div>

        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-bold text-indigo-900 mb-1 text-sm">Tenant-Scoped Access</h3>
                <p className="text-xs text-indigo-700">You can only view and manage content for your assigned tenant. Contact Super Admin to modify other tenants.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="markers" className="text-xs sm:text-sm">
              <Upload className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Markers</span>
            </TabsTrigger>
            <TabsTrigger value="quizzes" className="text-xs sm:text-sm">
              <FileText className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Quizzes</span>
            </TabsTrigger>
            <TabsTrigger value="textbooks" className="text-xs sm:text-sm">
              <BookOpen className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Content</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="markers" className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-indigo-900">AR Markers & 3D Models</h2>
              <Dialog open={showMarkerDialog} onOpenChange={setShowMarkerDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" size="sm">
                    <Plus className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Upload Marker</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Upload AR Marker & 3D Model</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUploadMarker} className="space-y-4">
                    {/* Content Location hierarchy */}
                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Content Location</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Board</Label>
                          <select className="w-full h-9 px-3 border rounded-md text-sm" value={filterBoard} onChange={e => { setFilterBoard(e.target.value); setFilterClass(''); setFilterSubject(''); setFilterTextbook(''); }}>
                            <option value="">All boards</option>
                            {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Class</Label>
                          <select className="w-full h-9 px-3 border rounded-md text-sm" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSubject(''); setFilterTextbook(''); }}>
                            <option value="">All classes</option>
                            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Subject</Label>
                          <select className="w-full h-9 px-3 border rounded-md text-sm" value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setFilterTextbook(''); }}>
                            <option value="">All subjects</option>
                            {subjects.filter(s => !filterClass || s.class_id === filterClass).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs">Textbook</Label>
                          <select className="w-full h-9 px-3 border rounded-md text-sm" value={filterTextbook} onChange={e => setFilterTextbook(e.target.value)}>
                            <option value="">Select textbook</option>
                            {textbooks.filter(t => (!filterBoard || t.board_id === filterBoard) && (!filterClass || t.class_id === filterClass) && (!filterSubject || t.subject_id === filterSubject)).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Chapter *</Label>
                        <select name="chapter_id" required className="w-full h-9 px-3 border rounded-md text-sm border-indigo-300 bg-white">
                          <option value="">Select chapter</option>
                          {chapters.filter(c => !filterTextbook || c.textbook_id === filterTextbook).map(c => <option key={c.id} value={c.id}>{c.title} (Ch. {c.chapter_number})</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Marker Details</p>
                        <div>
                          <Label className="text-xs">Concept Title *</Label>
                          <Input name="concept_title" required placeholder="e.g., DNA Structure" />
                        </div>
                        <div>
                          <Label className="text-xs">Description</Label>
                          <textarea name="concept_description" className="w-full h-20 px-3 py-2 border rounded-md text-sm" placeholder="Brief explanation of the concept" />
                        </div>
                        <div>
                          <Label className="text-xs">Image File * (JPG, PNG)</Label>
                          <Input name="file" type="file" accept="image/*" required onChange={handleMarkerFileChange} />
                          {markerPreview && <img src={markerPreview} alt="Preview" className="mt-2 w-full h-32 object-cover rounded border" />}
                        </div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">3D Model (Optional)</p>
                        <div>
                          <Label className="text-xs">Model Name</Label>
                          <Input name="model_name" placeholder="e.g., DNA 3D Model" />
                        </div>
                        <div>
                          <Label className="text-xs">Model File (.glb, .gltf)</Label>
                          <Input name="model_file" type="file" accept=".glb,.gltf,.obj,.fbx" onChange={handleModelFileChange} />
                          {modelPreview && <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">Selected: {modelPreview}</div>}
                        </div>
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                          <p className="text-xs text-amber-800"><strong>Note:</strong> 3D model is optional. You can upload it later.</p>
                        </div>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Upload Marker & Model</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {markers.map((marker) => (
                <Card key={marker.id} className="bg-white border-slate-200">
                  <CardContent className="p-4">
                    <div className="aspect-video bg-slate-100 rounded-lg mb-3 overflow-hidden">
                      <img src={marker.image_url} alt={marker.concept_title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-sm text-indigo-900">{marker.concept_title}</h3>
                      {marker.is_published ? (
                        <Badge className="text-xs bg-green-100 text-green-800 border-0">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Published
                        </Badge>
                      ) : (
                        <Badge className="text-xs bg-slate-100 text-slate-800 border-0">
                          <XCircle className="w-3 h-3 mr-1" />
                          Draft
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mb-3 line-clamp-2">{marker.concept_description}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handlePublishMarker(marker.id, marker.is_published)}
                        className={`flex-1 text-xs ${marker.is_published ? 'bg-slate-600 hover:bg-slate-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                      >
                        {marker.is_published ? 'Unpublish' : 'Publish'}
                      </Button>
                      {!quizzes.find(q => q.marker_id === marker.id) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedMarker(marker);
                            setShowQuizDialog(true);
                          }}
                          className="text-xs"
                        >
                          Add Quiz
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="quizzes" className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-indigo-900">Quizzes</h2>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {quizzes.map((quiz) => {
                const marker = markers.find(m => m.id === quiz.marker_id);
                return (
                  <Card key={quiz.id} className="bg-white border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-indigo-900 mb-1">{marker?.concept_title || 'Unknown Marker'}</h3>
                          <p className="text-sm text-slate-600">{quiz.questions.length} questions</p>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Quiz for: {selectedMarker?.concept_title}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateQuiz} className="space-y-6">
                  <input type="hidden" name="marker_id" value={selectedMarker?.id} />
                  {[0, 1, 2].map((qIndex) => (
                    <div key={qIndex} className="p-4 border border-slate-200 rounded-lg space-y-3">
                      <Label className="font-bold">Question {qIndex + 1}</Label>
                      <Input 
                        name={`question_${qIndex}`} 
                        placeholder="Enter question" 
                        required={qIndex === 0}
                        className="border-slate-300"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        {[0, 1, 2, 3].map((oIndex) => (
                          <Input 
                            key={oIndex}
                            name={`q${qIndex}_option_${oIndex}`}
                            placeholder={`Option ${oIndex + 1}`}
                            required={qIndex === 0}
                            className="border-slate-300"
                          />
                        ))}
                      </div>
                      <div>
                        <Label className="text-sm">Correct Answer</Label>
                        <select 
                          name={`q${qIndex}_correct`} 
                          required={qIndex === 0}
                          className="w-full h-10 px-3 border border-slate-300 rounded-md"
                        >
                          <option value="0">Option 1</option>
                          <option value="1">Option 2</option>
                          <option value="2">Option 3</option>
                          <option value="3">Option 4</option>
                        </select>
                      </div>
                    </div>
                  ))}
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Create Quiz</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="textbooks" className="space-y-4 mt-6">
            <h2 className="text-xl font-bold text-indigo-900">Textbooks & Chapters</h2>
            <div className="grid grid-cols-1 gap-4">
              {textbooks.map((textbook) => (
                <Card key={textbook.id} className="bg-white border-slate-200">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-indigo-900 mb-2">{textbook.title}</h3>
                    <p className="text-sm text-slate-600">{textbook.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};